import type { App } from 'vue'
import { buildInfo } from './buildInfo'

// Klientfejl-rapportering (D-014, fase 3). Frontenden havde indtil nu ingen logging
// overhovedet: ingen window.onerror, ingen app.config.errorHandler, og hver eneste
// catch-blok enten slugte fejlen eller viste den lokalt. Man kunne altså ikke se en
// eneste fejl hos en bruger.
//
// Fejl lægges i en lille kø og sendes samlet til BFF'ens /api/telemetry, som logger dem
// videre til App Insights. Alt er fire-and-forget: rapportering må aldrig kunne vælte
// appen eller forsinke noget, brugeren venter på.

const ENDPOINT = '/api/telemetry'
/** Hårdt loft pr. session: en fejl i en render-loop må ikke kunne spamme hverken netværk eller regning. */
const MAX_PER_SESSION = 20
const MAX_BATCH = 10
const FLUSH_DELAY_MS = 2000
const MAX_MESSAGE = 300
const MAX_STACK = 3000

export type ErrorKind = 'vue' | 'window' | 'promise' | 'sw' | 'api'

export interface ErrorContext {
  kind?: ErrorKind
  [key: string]: string | number | boolean | undefined
}

interface ClientError extends ErrorContext {
  message: string
  stack?: string
  route: string
  at: string
}

const queue: ClientError[] = []
const seen = new Set<string>()
let reported = 0
let timer: ReturnType<typeof setTimeout> | undefined

// Tilfældigt id pr. faneblad-session. Bevidst IKKE bruger-id'et: det er nok til at samle én
// brugers fejl i ét forløb, uden at loggen kan pege på personen.
const SESSION_KEY = 'telemetry-session'
let fallbackSessionId: string | undefined

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    // Privat browsing kan afvise sessionStorage. Så holder id'et bare i hukommelsen.
    return (fallbackSessionId ??= crypto.randomUUID())
  }
}

function truncate(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

/** Pakker det ud af en fejl, der kan sendes videre — også når det kastede ikke var en Error. */
function describe(err: unknown): { message: string; stack?: string } {
  if (err instanceof Error) {
    return {
      message: truncate(err.message || err.name, MAX_MESSAGE),
      stack: err.stack ? truncate(err.stack, MAX_STACK) : undefined,
    }
  }
  if (typeof err === 'string') return { message: truncate(err, MAX_MESSAGE) }
  try {
    return { message: truncate(JSON.stringify(err) ?? String(err), MAX_MESSAGE) }
  } catch {
    return { message: truncate(String(err), MAX_MESSAGE) }
  }
}

export function reportError(err: unknown, context: ErrorContext = {}): void {
  // Hele funktionen er pakket ind: en fejl her ville i værste fald udløse sig selv.
  try {
    if (reported >= MAX_PER_SESSION) return

    const { message, stack } = describe(err)
    // Den samme fejl i en loop skal ikke bruge alle 20 pladser.
    const fingerprint = `${context.kind ?? 'window'}|${message}|${stack?.slice(0, 120) ?? ''}`
    if (seen.has(fingerprint)) return
    seen.add(fingerprint)
    reported++

    queue.push({
      kind: 'window',
      ...context,
      message,
      stack,
      route: location.pathname,
      at: new Date().toISOString(),
    })

    if (queue.length >= MAX_BATCH) flushTelemetry()
    else schedule()
  } catch {
    // Med vilje tom: rapportering må aldrig kaste videre op i appen.
  }
}

function schedule(): void {
  if (timer !== undefined) return
  timer = setTimeout(flushTelemetry, FLUSH_DELAY_MS)
}

export function flushTelemetry(): void {
  if (timer !== undefined) {
    clearTimeout(timer)
    timer = undefined
  }
  if (queue.length === 0) return

  const events = queue.splice(0, MAX_BATCH)
  const body = JSON.stringify({
    sessionId: sessionId(),
    // Commit'et, ikke udgivelsesnummeret: en fejlrapport skal pege paa den praecise bundle.
    version: buildInfo.commit,
    // i18n sætter <html lang> ved opstart og ved sprogskift, så den er sandheden her.
    locale: document.documentElement.lang || 'da',
    online: navigator.onLine,
    events,
  })

  try {
    const blob = new Blob([body], { type: 'application/json' })
    // sendBeacon overlever at siden lukkes; det gør en almindelig fetch ikke.
    if (!navigator.sendBeacon?.(ENDPOINT, blob)) {
      void fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // Kunne ikke sendes. Fejlen er tabt — det er stadig bedre end at forstyrre brugeren.
  }

  if (queue.length > 0) schedule()
}

/**
 * Kobler rapporteringen på de steder, fejl faktisk slipper ud: Vues egen fejl-hook,
 * ubehandlede exceptions og afviste promises.
 */
export function installTelemetry(app: App): void {
  app.config.errorHandler = (err, _instance, info) => {
    reportError(err, { kind: 'vue', info })
    // Vue logger ikke selv, når der er en handler — behold signalet i konsollen.
    console.error(err)
  }

  window.addEventListener('error', (event) => {
    // Ressourcefejl (billede/script der ikke kan hentes) har hverken error eller message
    // og fortæller intet brugbart.
    if (!event.error && !event.message) return
    reportError(event.error ?? event.message, { kind: 'window' })
  })

  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { kind: 'promise' })
  })

  // Sidste chance for at få køen afsted, inden siden lukkes eller skjules.
  window.addEventListener('pagehide', flushTelemetry)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTelemetry()
  })
}
